/**
 * Star coordinates for the home hero sky.
 *
 * These lived in `features/scenes/scene-data.ts` for as long as the poem
 * illustrations were assembled from scattered parts and shared a star field with
 * the hero. The illustrations are single vendored icons now and scatter nothing,
 * so the whole table set went with them and this one followed its last consumer
 * into the feature that owns it. A shared module with one importer is not shared.
 *
 * AUTHORED, NOT GENERATED. The design placed each star with an FNV-1a-seeded
 * PRNG. In a prerendered site the output is frozen at build time anyway, so the
 * PRNG bought nothing while costing determinism, diffability and testability —
 * and `Math.random` is a separate ESLint error. Twenty rows, hand-placed, is
 * smaller than the generator it replaced and is directly assertable.
 */

export const SKY_WIDTH = 1200
export const SKY_HEIGHT = 620

export interface StarPlacement {
  readonly x: number
  readonly y: number
  /** Radius multiplier applied via the `r` presentation attribute. */
  readonly s: number
}

/**
 * `sky.tsx` takes the first 20. The extra rows are headroom for a denser hero,
 * and `sky-data.test.ts` enforces that the table stays at least as long as the
 * slice — a `slice` past the end is not an error, it just draws fewer stars.
 *
 * Every row lies inside the viewBox, and `y` is compressed further at render time
 * so the field sits in the hero's upper band rather than behind the greeting.
 */
export const STAR_PLACEMENTS: readonly StarPlacement[] = Object.freeze([
  { x: 96, y: 88, s: 2.6 },
  { x: 248, y: 152, s: 1.8 },
  { x: 392, y: 74, s: 3.1 },
  { x: 540, y: 196, s: 1.5 },
  { x: 688, y: 108, s: 2.3 },
  { x: 836, y: 168, s: 2.9 },
  { x: 972, y: 82, s: 1.7 },
  { x: 1112, y: 144, s: 2.5 },
  { x: 168, y: 244, s: 2.1 },
  { x: 316, y: 312, s: 1.6 },
  { x: 464, y: 268, s: 2.8 },
  { x: 612, y: 336, s: 1.9 },
  { x: 760, y: 256, s: 2.4 },
  { x: 908, y: 324, s: 1.4 },
  { x: 1048, y: 276, s: 3.0 },
  { x: 60, y: 392, s: 2.0 },
  { x: 380, y: 432, s: 1.7 },
  { x: 1150, y: 404, s: 2.2 },
  { x: 212, y: 476, s: 1.5 },
  { x: 528, y: 132, s: 2.7 },
  { x: 676, y: 420, s: 1.8 },
  { x: 820, y: 468, s: 2.2 },
  { x: 964, y: 396, s: 1.6 },
  { x: 136, y: 156, s: 1.9 },
])
